import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import type { SegmentDimension, SegmentIncomeStatementResponse, SegmentRow } from '@accounting/shared';
import { useSegmentIncomeStatement } from '../../hooks/useSegmentReport';
import { useActiveCompanyId } from '../../stores/useCompanyStore';
import { DateRangePicker } from '../../components/reports/PeriodFilter';
import { formatCurrency, todayIso } from '../../lib/formatters';

export const Route = createFileRoute('/_auth/reports/segment-income-statement')({
  component: SegmentIncomeStatement,
});

function sumRecord(rec: Record<string, string>): string {
  return Object.values(rec).reduce((s, v) => s + parseFloat(v || '0'), 0).toFixed(2);
}
function rowTotal(amounts: Record<string, string>): string {
  return Object.values(amounts).reduce((s, v) => s + parseFloat(v || '0'), 0).toFixed(2);
}

function SegmentIncomeStatement() {
  const companyId = useActiveCompanyId();
  const [dimension, setDimension] = useState<SegmentDimension>('class');
  const [from, setFrom] = useState(`${todayIso().slice(0, 4)}-01-01`);
  const [to, setTo] = useState(todayIso());

  const { data, isLoading, error } = useSegmentIncomeStatement(companyId, from, to, dimension);

  if (!companyId) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: 32 }}>
        <h1 style={{ marginTop: 0 }}>P&amp;L by Segment</h1>
        <p className="muted">No active company.</p>
      </div>
    );
  }

  return (
    <div>
      <Link to="/reports" style={{ fontSize: 13 }}>← Reports</Link>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '8px 0 16px', flexWrap: 'wrap', gap: 12 }}>
        <h1 style={{ fontSize: 24, margin: 0 }}>P&amp;L by {dimension === 'class' ? 'Class' : 'Location'}</h1>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select value={dimension} onChange={(e) => setDimension(e.target.value as SegmentDimension)}>
            <option value="class">By Class</option>
            <option value="location">By Location</option>
          </select>
          <DateRangePicker from={from} to={to} onChange={(r) => { setFrom(r.from); setTo(r.to); }} />
        </div>
      </div>

      {isLoading && <p className="muted">Loading…</p>}
      {error && <p className="field-error">Failed to load the report.</p>}
      {data && data.segments.length === 0 && (
        <div className="card" style={{ textAlign: 'center', padding: 32 }}>
          <p className="muted" style={{ margin: 0 }}>No income or expense activity tagged with a {dimension} in this period.</p>
        </div>
      )}

      {data && data.segments.length > 0 && <SegmentTable data={data} />}
    </div>
  );
}

function SegmentTable({ data }: { data: SegmentIncomeStatementResponse }) {
  const cols = [...data.segments, { id: '__total', name: 'Total' }];

  const cell = (rec: Record<string, string>, segId: string) =>
    segId === '__total' ? sumRecord(rec) : rec[segId] ?? '0.00';

  const sectionRows = (type: string) => data.rows.filter((r) => r.accountType === type);
  const hasOtherIncome = sectionRows('other_income').length > 0;
  const hasOtherExpense = sectionRows('other_expense').length > 0;

  const DataRow = ({ row }: { row: SegmentRow }) => (
    <tr>
      <td style={{ paddingLeft: 20 }}>{row.code} · {row.name}</td>
      {cols.map((c) => (
        <td key={c.id} style={{ textAlign: 'right' }}>{formatCurrency(c.id === '__total' ? rowTotal(row.amounts) : (row.amounts[c.id] ?? '0.00'))}</td>
      ))}
    </tr>
  );
  const SummaryRow = ({ label, rec, strong }: { label: string; rec: Record<string, string>; strong?: boolean }) => (
    <tr style={{ fontWeight: strong ? 700 : 600, borderTop: '1px solid var(--border)' }}>
      <td>{label}</td>
      {cols.map((c) => (
        <td key={c.id} style={{ textAlign: 'right' }}>{formatCurrency(cell(rec, c.id))}</td>
      ))}
    </tr>
  );
  const SectionHead = ({ label }: { label: string }) => (
    <tr><td colSpan={cols.length + 1} style={{ fontWeight: 700, paddingTop: 10 }}>{label}</td></tr>
  );

  return (
    <div className="card" style={{ padding: 16, overflowX: 'auto' }}>
      <table className="table">
        <thead>
          <tr>
            <th></th>
            {cols.map((c) => <th key={c.id} style={{ textAlign: 'right' }}>{c.name}</th>)}
          </tr>
        </thead>
        <tbody>
          <SectionHead label="Revenue" />
          {sectionRows('revenue').map((r) => <DataRow key={r.code} row={r} />)}
          <SummaryRow label="Total Revenue" rec={data.summary.revenue} />

          {sectionRows('cogs').length > 0 && (
            <>
              <SectionHead label="Cost of Goods Sold" />
              {sectionRows('cogs').map((r) => <DataRow key={r.code} row={r} />)}
              <SummaryRow label="Total COGS" rec={data.summary.cogs} />
            </>
          )}

          <SummaryRow label="Gross Profit" rec={data.summary.grossProfit} strong />

          <SectionHead label="Expenses" />
          {sectionRows('expense').map((r) => <DataRow key={r.code} row={r} />)}
          <SummaryRow label="Total Expenses" rec={data.summary.expense} />

          {hasOtherIncome && (
            <>
              <SectionHead label="Other Income" />
              {sectionRows('other_income').map((r) => <DataRow key={r.code} row={r} />)}
              <SummaryRow label="Total Other Income" rec={data.summary.otherIncome} />
            </>
          )}
          {hasOtherExpense && (
            <>
              <SectionHead label="Other Expense" />
              {sectionRows('other_expense').map((r) => <DataRow key={r.code} row={r} />)}
              <SummaryRow label="Total Other Expense" rec={data.summary.otherExpense} />
            </>
          )}

          <SummaryRow label="Net Income" rec={data.summary.netIncome} strong />
        </tbody>
      </table>
    </div>
  );
}
